import type { ColorScheme } from '@unifyapps/defs/types/interface';
import { INTERFACE_THEME, UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { StyleOverride } from '../../styles/StyleOverride';

type ThemeOverrideContextType = {
  themeOverrideClassName: string;
  /**
   * Scheme to be applied to the theme override
   */
  scheme?: ColorScheme;
};

export const ThemeOverrideContext = createContext<ThemeOverrideContextType>({
  themeOverrideClassName: '',
  scheme: undefined,
});

export function useThemeOverrideContext() {
  return useContext(ThemeOverrideContext);
}

type ThemeOverrideProviderProps = {
  style: string | undefined;
  scheme?: ColorScheme;
  children: React.ReactNode;
};

/**
 * Provides theme override provider for Interface Builder and Preview
 *
 */
export function ThemeOverrideContextProvider({
  style,
  scheme,
  children,
}: ThemeOverrideProviderProps) {
  const themeOverrideClassName = `${UA_THEME_VARS} ${INTERFACE_THEME}`;

  const contextValue = useMemo(
    () => ({ themeOverrideClassName, scheme }),
    [themeOverrideClassName, scheme],
  );

  return (
    <ThemeOverrideContext.Provider value={contextValue}>
      {style ? <StyleOverride style={style} /> : null}
      {children}
    </ThemeOverrideContext.Provider>
  );
}
